import { useCallback, useEffect, useMemo, useState } from "react"

import type { CoviThreadMeta } from "../model/types"

/** Drop ids whose thread no longer exists (deleted, pruned, org switch). */
function retainLive(
  previous: Set<string>,
  liveThreadIds: Set<string>,
): Set<string> {
  const next = new Set(
    [...previous].filter((threadId) => liveThreadIds.has(threadId)),
  )
  return next.size === previous.size ? previous : next
}

function useThreadFlagSet(threads: CoviThreadMeta[]) {
  const [threadIds, setThreadIds] = useState<Set<string>>(() => new Set())

  const setFlag = useCallback((threadId: string, flagged: boolean) => {
    setThreadIds((previous) => {
      if (previous.has(threadId) === flagged) {
        return previous
      }
      const next = new Set(previous)
      if (flagged) {
        next.add(threadId)
      } else {
        next.delete(threadId)
      }
      return next
    })
  }, [])

  useEffect(() => {
    const liveThreadIds = new Set(threads.map((thread) => thread.id))
    setThreadIds((previous) => retainLive(previous, liveThreadIds))
  }, [threads])

  return [threadIds, setFlag] as const
}

/**
 * Holds transient per-thread activity outside the persisted history registry.
 *
 * A stream remains active while its conversation is behind another Home thread
 * or the Daily Briefing, but no activity flag survives a reload or deletion.
 *
 * Drafts live here rather than in the panel because the prune that runs on
 * "New chat" happens in the provider: residency has to be visible to whoever
 * creates a thread, not only to whoever renders one.
 */
export function useCoviThreadActivity(
  threads: CoviThreadMeta[],
  activeThreadId: string | null,
) {
  const [streamingThreadIds, setThreadStreaming] = useThreadFlagSet(threads)
  const [draftThreadIds, setThreadDraft] = useThreadFlagSet(threads)

  // The threads that must survive a prune and stay mounted: the one on screen,
  // plus any still working or holding an unsent draft.
  const residentThreadIds = useMemo(() => {
    const resident = new Set([...streamingThreadIds, ...draftThreadIds])
    if (activeThreadId) {
      resident.add(activeThreadId)
    }
    return resident
  }, [activeThreadId, draftThreadIds, streamingThreadIds])

  return {
    streamingThreadIds,
    setThreadStreaming,
    draftThreadIds,
    setThreadDraft,
    residentThreadIds,
  }
}
